<template>
  <div class="our-business-page">
    <!-- Hero -->
    <section class="business-hero">
      <div class="container hero-grid">
        <div class="hero-copy">
          <p class="eyebrow">GLOBAL AI LINK LIMITED</p>
          <h1>Our Business</h1>
          <p class="hero-desc">
            We provide global AI marketing and technology services, covering app
            development, ad systems, compliance consultancy and cross-border
            growth operations.
          </p>
          <div class="hero-stats">
            <div v-for="stat in heroStats" :key="stat.label" class="stat-item">
              <strong>{{ stat.value }}</strong>
              <span>{{ stat.label }}</span>
            </div>
          </div>
        </div>
        <div class="hero-image-wrap">
          <img
            src="../../static/images/globalai001.webp"
            alt="Global digital business"
          />
        </div>
      </div>
    </section>

    <!-- Business at a glance -->
    <section class="business-nav-section">
      <div class="container">
        <div class="business-nav-grid">
          <a
            v-for="item in businessNav"
            :key="item.id"
            :href="'#service-' + item.id"
            class="business-nav-item"
          >
            <span class="nav-index">{{
              String(item.id).padStart(2, "0")
            }}</span>
            <span class="nav-label">{{ item.shortTitle }}</span>
          </a>
        </div>
      </div>
    </section>

    <!-- Core Services 1–4 -->
    <section class="core-section">
      <div class="container">
        <div class="section-head">
          <h2>Technology &amp; Marketing Services</h2>
          <p>
            End-to-end product development, ad systems and cross-border growth
            solutions built in-house for global brands.
          </p>
        </div>
        <div class="services-grid">
          <article
            v-for="service in coreServices"
            :key="service.id"
            :id="'service-' + service.id"
            class="service-card"
          >
            <div class="service-banner">
              <img :src="service.banner" :alt="service.title" />
              <span class="service-badge">{{ service.category }}</span>
            </div>
            <div class="service-body">
              <div class="service-head">
                <div class="service-icon-box">
                  <img
                    :src="service.icon"
                    :alt="service.title + ' icon'"
                    class="service-icon"
                  />
                </div>
                <div class="service-title-wrap">
                  <span class="service-num" aria-hidden="true">{{ String(service.id).padStart(2, "0") }}</span>
                  <h3>{{ service.title }}</h3>
                </div>
              </div>
              <p>{{ service.desc }}</p>
              <div class="tag-list">
                <span v-for="tag in service.tags" :key="tag" class="tag">{{
                  tag
                }}</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Featured Media Buy 5–6 -->
    <section class="featured-section">
      <div class="container">
        <div class="section-head">
          <h2>Media Buy &amp; Performance Operations</h2>
          <p>
            Large-scale in-house teams delivering search arbitrage and lead
            generation with full compliance and tracking integration.
          </p>
        </div>

        <article
          v-for="(item, index) in featuredServices"
          :key="item.id"
          :id="'service-' + item.id"
          :class="[
            'featured-block',
            { 'featured-block--reverse': index % 2 === 1 },
          ]"
        >
          <div class="featured-visual">
            <img :src="item.banner" :alt="item.title" />
            <div class="featured-stat-bar">
              <div
                v-for="stat in item.stats"
                :key="stat.label"
                class="featured-stat"
              >
                <strong>{{ stat.value }}</strong>
                <span>{{ stat.label }}</span>
              </div>
            </div>
          </div>
          <div class="featured-content">
            <span class="featured-category">{{ item.category }}</span>
            <div class="service-title-wrap">
              <span class="service-num" aria-hidden="true">{{ String(item.id).padStart(2, "0") }}</span>
              <h3>{{ item.title }}</h3>
              <router-link
                v-if="item.id === 5"
                to="/search-arbitrage"
                class="details-btn"
              >
                Details <span>&rarr;</span>
              </router-link>
              <router-link
                v-else-if="item.id === 6"
                to="/leadgen"
                class="details-btn"
              >
                Details <span>&rarr;</span>
              </router-link>
            </div>
            <ul class="featured-list">
              <li v-for="point in item.highlights" :key="point">{{ point }}</li>
            </ul>
            <div class="tag-list">
              <span
                v-for="tag in item.tags"
                :key="tag"
                class="tag tag--light"
                >{{ tag }}</span
              >
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "OurBusinessPage",
  data() {
    return {
      heroStats: [
        { value: "6", label: "Core Business Lines" },
        { value: "30+", label: "Media Buyers" },
        { value: "Global", label: "Cross-border Reach" },
      ],
      businessNav: [
        { id: 1, shortTitle: "App Development" },
        { id: 2, shortTitle: "Ad Systems" },
        { id: 3, shortTitle: "Apple Compliance" },
        { id: 4, shortTitle: "Digital Marketing" },
        { id: 5, shortTitle: "Search Arbitrage" },
        { id: 6, shortTitle: "LeadGen" },
      ],
      coreServices: [
        {
          id: 1,
          category: "Technology",
          title: "Custom iOS & Android App Development",
          desc: "We build customized native utility and marketing-oriented mobile apps for global clients, fully compliant with official review rules of Apple App Store and Google Play Store. Our full-cycle services cover original UI/UX visual design, customized functional development, platform compliance modification, bug debugging, as well as official app listing and publishing. Adopting stable native coding framework, we deliver secure, smooth, enterprise-grade finished mobile applications tailored to business operation, brand promotion and user operation demands, and provide long-term post-launch maintenance and version update support.",
          icon: require("../../static/images/publisher/feature-icon-1.png"),
          banner:
            "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80",
          tags: ["iOS", "Android", "App Store"],
        },
        {
          id: 2,
          category: "Technology",
          title: "AI Programmatic Advertising System Development",
          desc: "We develop independent AI-powered programmatic advertising systems tailored for cross-border ad practitioners. It features proprietary traffic attribution algorithm, intelligent auto-bidding module and real-time conversion callback tools, which is fully compatible with Meta, TikTok, CTV and other mainstream global advertising platforms. We build customized self-hosted ad tracking dashboards to realize full-link data monitoring, accurate campaign data analytics, traffic quality verification and ad risk warning. This in-house system helps clients optimize ad delivery strategies, cut invalid ad costs and boost overall advertising conversion efficiency steadily.",
          icon: require("../../static/images/publisher/feature-icon-2.png"),
          banner:
            "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80",
          tags: ["Meta", "TikTok", "CTV", "Attribution"],
        },
        {
          id: 3,
          category: "Consulting",
          title: "Apple Developer Compliance Consultancy",
          desc: "We provide one-stop professional compliance consultancy exclusively for Hong Kong registered enterprises targeting Apple ecosystem services. We offer full-process tailored guidance covering Apple Developer Enterprise account application, official document sorting, onsite audit preparation, app content compliance revision, policy risk assessment and long-term secure account maintenance. All consulting schemes and adjustment solutions are fully aligned with the latest Apple official audit rules and regional platform policies, helping enterprises avoid account rejection, suspension or ban, and ensure stable and compliant app release and operation.",
          icon: require("../../static/images/publisher/feature-icon-3.png"),
          banner:
            "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1200&q=80",
          tags: ["Apple Developer", "Compliance", "Audit"],
        },
        {
          id: 4,
          category: "Marketing",
          title: "Cross-border Brand Digital Marketing",
          desc: "We deliver tailored end-to-end cross-border digital marketing solutions for global brand clients covering e-commerce platforms, utility mobile applications and casual mobile games. Our core services include full-cycle global traffic operation, AI-driven precise audience portrait screening, intelligent audience optimization, automated ad creative generation and multi-platform compliant ad deployment. We support unified delivery and data management on mainstream global social and ad platforms, lower brand overseas operation costs, enhance regional brand exposure, and achieve stable user acquisition, order conversion and long-term brand overseas growth.",
          icon: require("../../static/images/publisher/feature-icon-4.png"),
          banner:
            "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1200&q=80",
          tags: ["E-commerce", "UA", "Creative AI"],
        },
      ],
      featuredServices: [
        {
          id: 5,
          category: "Media Buy",
          title: "Search Arbitrage (RSOC/AFS)",
          banner:
            "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
          stats: [
            { value: "30+", label: "Media Buyers" },
            { value: "10", label: "Designers" },
            { value: "100%", label: "Compliance Check" },
          ],
          highlights: [
            "We have over 30 media buyers, 10 creative designers, 4 account managers, 2 fully technical support and all of them have 5-8 years of experience in campaign optimization and 4 years in search arbitrage on average.",
            "We support Facebook, Tiktok and Taboola platforms.",
            "We also have set up an in-house compliance team who will 100% check all the media-buyers' creatives and make sure its fully comply with Google RAF requirements.",
          ],
          tags: ["RSOC/AFS", "Facebook", "TikTok", "Taboola"],
        },
        {
          id: 6,
          category: "Media Buy",
          title: "Lead-Gen Business",
          banner:
            "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
          stats: [
            { value: "15+", label: "LeadGen Buyers" },
            { value: "5+", label: "Years Experience" },
            { value: "US/EU", label: "Top GEOs" },
          ],
          highlights: [
            "We are a big media-buyer team and have 15+ around fully in-house media-buyers who all in LeadGen campaigns. each of them have more than 4-5 years LeadGen working experience",
            "We support: Facebook, Tiktok, Taboola, Outbrain, MediaGo.",
            "Our top verticals: Homeservices, Sweepstakes, Nutra, Personal loan",
            "Our Top GEOS: US and European countries",
            "We can do prelanders pages by ourselves",
            "We also support Clickflare, Voluum tracking system integration",
          ],
          tags: ["CPL", "Pre-lander", "Voluum", "Clickflare"],
        },
      ],
    };
  },
};
</script>

<style scoped>
.our-business-page {
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  color: #1c2b4e;
  background-color: #f0f7ff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero */
.business-hero {
  padding: 140px 0 56px;
  background-image: url("../../static/images/publisher/hero-bg.jpg");
  background-size: cover;
  background-position: center top;
  background-attachment: fixed;
  background-repeat: no-repeat;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 36px;
  align-items: center;
}

.hero-copy {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(75, 137, 255, 0.14);
  border-radius: 20px;
  box-shadow: 0 12px 36px rgba(28, 43, 78, 0.08);
  padding: 36px 38px;
}

.eyebrow {
  margin: 0 0 12px;
  font-size: 13px;
  letter-spacing: 1.6px;
  color: #4b89ff;
  font-weight: 700;
}

.hero-copy h1 {
  margin: 0 0 14px;
  font-size: 48px;
  line-height: 1.12;
  font-weight: 700;
}

.hero-desc {
  margin: 0 0 24px;
  font-size: 17px;
  line-height: 1.75;
  color: #475569;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid rgba(75, 137, 255, 0.12);
}

.stat-item {
  text-align: center;
}

.stat-item strong {
  display: block;
  font-size: 26px;
  font-weight: 700;
  color: #296dff;
  line-height: 1.2;
}

.stat-item span {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.hero-image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-image-wrap img {
  width: 80%;
  max-width: 480px;
  height: auto;
  /* filter: drop-shadow(0 16px 32px rgba(75, 137, 255, 0.12)); */
  border-radius: 50%;
}

/* Business nav */
.business-nav-section {
  padding: 0 0 48px;
  margin-top: -20px;
  position: relative;
  z-index: 2;
}

.business-nav-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
  padding: 24px 28px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(28, 43, 78, 0.1);
  border: 1px solid rgba(75, 137, 255, 0.1);
}

.business-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 10px;
  border-radius: 12px;
  text-decoration: none;
  transition: background 0.25s ease, transform 0.25s ease;
}

.business-nav-item:hover {
  background: rgba(75, 137, 255, 0.08);
  transform: translateY(-2px);
}

.nav-index {
  font-size: 24px;
  font-weight: 700;
  color: #4b89ff;
  line-height: 1;
}

.nav-label {
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  text-align: center;
  line-height: 1.4;
}

/* Section head */
.section-head {
  text-align: center;
  max-width: 680px;
  margin: 0 auto 48px;
}

.section-head h2 {
  margin: 0 0 14px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.2;
}

.section-head h2::after {
  content: "";
  display: block;
  width: 64px;
  height: 3px;
  margin: 16px auto 0;
  background: linear-gradient(90deg, #4b89ff, #296dff);
  border-radius: 2px;
}

.section-head p {
  margin: 20px 0 0;
  font-size: 16px;
  line-height: 1.75;
  color: #64748b;
}

/* Core services */
.core-section {
  padding: 72px 0 80px;
  background: #ffffff;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
}

.service-card {
  scroll-margin-top: 100px;
  background: #ffffff;
  border: 1px solid rgba(75, 137, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(28, 43, 78, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.service-card:hover {
  transform: translateY(-6px);
  border-color: rgba(75, 137, 255, 0.2);
  box-shadow: 0 20px 48px rgba(75, 137, 255, 0.1);
}

.service-banner {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.service-banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.service-card:hover .service-banner img {
  transform: scale(1.06);
}

.service-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #ffffff;
  background: rgba(41, 109, 255, 0.9);
  border-radius: 999px;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.service-body {
  padding: 32px 32px 36px;
}

.service-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 18px;
}

.service-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, rgba(75, 137, 255, 0.12) 0%, rgba(75, 137, 255, 0.04) 100%);
  border-radius: 16px;
}

.service-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.service-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.service-num {
  font-size: 18px;
  font-weight: 700;
  color: #4b89ff;
  opacity: 0.9;
  letter-spacing: 0.5px;
}

.service-body h3, .featured-content h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.35;
  color: #1c2b4e;
  letter-spacing: -0.3px;
}

.details-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #4b89ff;
  background-color: rgba(75, 137, 255, 0.08);
  border-radius: 999px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.details-btn span {
  transition: transform 0.3s ease;
}

.details-btn:hover {
  background-color: #4b89ff;
  color: #ffffff;
}

.details-btn:hover span {
  transform: translateX(3px);
}

.service-body p {
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.75;
  color: #64748b;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #296dff;
  background: rgba(75, 137, 255, 0.08);
  border-radius: 999px;
  letter-spacing: 0.3px;
}

.tag--light {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.2);
}

/* Featured */
.featured-section {
  padding: 72px 0 96px;
  background: linear-gradient(180deg, #f0f8ff 0%, #ffffff 100%);
}

.featured-block {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: stretch;
  margin-bottom: 32px;
  scroll-margin-top: 100px;
}

.featured-block:last-child {
  margin-bottom: 0;
}

.featured-block--reverse .featured-visual {
  order: 2;
}

.featured-block--reverse .featured-content {
  order: 1;
}

.featured-visual {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 14px 36px rgba(28, 43, 78, 0.12);
  min-height: 320px;
}

.featured-visual img {
  width: 100%;
  height: 100%;
  min-height: 320px;
  object-fit: cover;
  display: block;
}

.featured-stat-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
}

.featured-stat {
  padding: 14px 10px;
  text-align: center;
  background: rgba(28, 43, 78, 0.72);
}

.featured-stat strong {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}

.featured-stat span {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
}

.featured-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px 36px;
  background: #ffffff;
  border: 1px solid rgba(75, 137, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 12px 32px rgba(28, 43, 78, 0.04);
}

.featured-category {
  display: inline-block;
  width: fit-content;
  margin-bottom: 14px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #4b89ff;
  background: rgba(75, 137, 255, 0.1);
  border-radius: 999px;
}

.featured-content .service-title-wrap {
  margin-bottom: 18px;
}

.featured-content h3 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: #1c2b4e;
  letter-spacing: -0.4px;
}

@media (max-width: 480px) {
  .details-btn {
    width: 100%;
    justify-content: center;
    margin-left: 0;
    margin-top: 10px;
  }
}

.featured-list {
  list-style: none;
  margin: 0 0 24px;
  padding: 0;
}

.featured-list li {
  position: relative;
  padding-left: 18px;
  margin-bottom: 12px;
  font-size: 15px;
  line-height: 1.7;
  color: #64748b;
}

.featured-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 9px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4b89ff;
}

.featured-list li:last-child {
  margin-bottom: 0;
}

.featured-content .tag--light {
  color: #296dff;
  background: rgba(75, 137, 255, 0.1);
}

@media (max-width: 1100px) {
  .business-nav-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .hero-copy h1 {
    font-size: 40px;
  }
}

@media (max-width: 992px) {
  .business-hero {
    padding-top: 116px;
  }

  .hero-grid,
  .services-grid,
  .featured-block,
  .featured-block--reverse {
    grid-template-columns: 1fr;
  }

  .featured-block--reverse .featured-visual,
  .featured-block--reverse .featured-content {
    order: initial;
  }

  .hero-copy h1 {
    font-size: 36px;
  }

  .section-head h2 {
    font-size: 30px;
  }

  .business-nav-section {
    margin-top: 0;
    padding-bottom: 32px;
  }
}

@media (max-width: 576px) {
  .hero-copy {
    padding: 24px 20px;
  }

  .hero-copy h1 {
    font-size: 30px;
  }

  .hero-stats {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .stat-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    padding: 8px 0;
    border-bottom: 1px solid rgba(75, 137, 255, 0.08);
  }

  .stat-item:last-child {
    border-bottom: none;
  }

  .stat-item strong {
    font-size: 22px;
  }

  .business-nav-grid {
    grid-template-columns: repeat(2, 1fr);
    padding: 18px 16px;
    gap: 12px;
  }

  .nav-index {
    font-size: 22px;
  }

  .nav-label {
    font-size: 14px;
  }

  .featured-content {
    padding: 24px 20px;
  }

  .featured-content h3 {
    font-size: 22px;
  }
}
</style>
